/**
 * Service Registry - Central Registration System
 * 
 * This module handles the registration of all services in the application,
 * providing a centralized way to configure dependency injection.
 */

import { ServiceContainer, SERVICE_KEYS } from './service-container';
import { 
  HttpService, 
  createHttpService,
  getAuthTokenFromStore,
  getStoreFromContainer,
  getRouterFromContainer
} from './http-service';

// Import all data service classes
import { AthleteData } from '../../athlete/athlete-data';
import { AthleteDataService } from '../../athlete/athlete-data-service';
import { CompetitionData } from '../../competition/competition-data';
import { ClubData } from '../../club/club-data';
import { ConfigData } from '../../config/config-data';
import { BuilderData } from '../../builder/builder-data';
import { EventData } from '../../event/event-data';
import { LocationData } from '../../location/location-data';
import { SecondaryData } from '../../secondary/secondary-data';
import { SecondaryCustData } from '../../secondary/cust/secondary-cust-data';
import { CheckinData } from '../../competition/checkin/checkin-data';
import { FieldHelpData } from '../../common/ui/field/field-help/field-help-data';
import { AthleteCompSchedData } from '../../athleteCompSched/athletecompsched-data';
import { CompEventTeamHeaderData } from '../../athleteCompSched/comp-event-teams/comp-event-team-header-data';

// Import all business service classes
import { AthleteService } from '../../athlete/athlete-service';
import { CompetitionService } from '../../competition/competiton-service';
import { ConfigService } from '../../config/config-service';
import { AthleteCompSchedService } from '../../athleteCompSched/athletecompsched-service';
import { SecondaryService } from '../../secondary/secondary-service';
import { CommonService } from '../../common/common-service';

/**
 * Register all HTTP services
 */
function registerHttpServices(container: ServiceContainer): void {
  // Register HTTP service with dependency injection
  container.register(SERVICE_KEYS.HTTP_SERVICE, () => {
    const httpService = createHttpService();
    
    // Configure dependency injection
    httpService.setAuthTokenProvider(getAuthTokenFromStore);
    httpService.setStoreProvider(getStoreFromContainer);
    httpService.setRouterProvider(getRouterFromContainer);
    
    return httpService;
  });
}

/**
 * Register all data services
 */
function registerDataServices(container: ServiceContainer): void {
  // Athlete data services
  container.register(SERVICE_KEYS.ATHLETE_DATA, () => new AthleteData());
  container.register(SERVICE_KEYS.ATHLETE_DATA_SERVICE, () => new AthleteDataService());
  
  // Competition data services
  container.register(SERVICE_KEYS.COMPETITION_DATA, () => new CompetitionData());
  
  // Club data services
  container.register(SERVICE_KEYS.CLUB_DATA, () => new ClubData());
  
  // Config data services
  container.register(SERVICE_KEYS.CONFIG_DATA, () => new ConfigData());
  
  // Builder data services
  container.register(SERVICE_KEYS.BUILDER_DATA, () => new BuilderData());
  
  // Event data services
  container.register(SERVICE_KEYS.EVENT_DATA, () => new EventData());
  
  // Location data services
  container.register(SERVICE_KEYS.LOCATION_DATA, () => new LocationData());
  
  // Secondary data services
  container.register(SERVICE_KEYS.SECONDARY_DATA, () => new SecondaryData());
  container.register(SERVICE_KEYS.SECONDARY_CUST_DATA, () => new SecondaryCustData());
  
  // Checkin data services
  container.register(SERVICE_KEYS.CHECKIN_DATA, () => new CheckinData());
  
  // Field help data services
  container.register(SERVICE_KEYS.FIELD_HELP_DATA, () => new FieldHelpData());
  
  // Athlete comp sched data services
  container.register(SERVICE_KEYS.ATHLETE_COMP_SCHED_DATA, () => new AthleteCompSchedData());
  container.register(SERVICE_KEYS.COMP_EVENT_TEAM_HEADER_DATA, () => new CompEventTeamHeaderData());
}

/**
 * Register all business services
 */
function registerBusinessServices(container: ServiceContainer): void {
  // Athlete services
  container.register(SERVICE_KEYS.ATHLETE_SERVICE, () => new AthleteService());
  
  // Competition services
  container.register(SERVICE_KEYS.COMPETITION_SERVICE, () => new CompetitionService());
  
  // Config services
  container.register(SERVICE_KEYS.CONFIG_SERVICE, () => new ConfigService());
  
  // Athlete comp sched services
  container.register(SERVICE_KEYS.ATHLETE_COMP_SCHED_SERVICE, () => new AthleteCompSchedService());
  
  // Secondary services
  container.register(SERVICE_KEYS.SECONDARY_SERVICE, () => new SecondaryService());
  
  // Common services
  container.register(SERVICE_KEYS.COMMON_SERVICE, () => new CommonService());
}

/**
 * Initialize all services in the service container
 * This should be called once during application startup
 */
export function initializeServices(): void {
  const container = ServiceContainer.getInstance();
  
  console.log('[ServiceRegistry] Initializing service container...');
  
  // Register services in dependency order
  registerHttpServices(container);
  registerBusinessServices(container);
  registerDataServices(container);
  
  console.log('[ServiceRegistry] Service registration complete. Registered services:', 
    container.getRegisteredServices());
}

/**
 * Configure runtime context for the service container
 * This should be called after store and router are created
 */
export function configureRuntimeContext(store: any, router: any): void {
  const container = ServiceContainer.getInstance();
  container.setRuntimeContext(store, router);
  
  console.log('[ServiceRegistry] Runtime context configured');
}

/**
 * Get a service from the container with type safety
 */
export function getRegisteredService<T>(key: string): T {
  return ServiceContainer.getInstance().get<T>(key);
}

/**
 * Convenience functions for commonly used services
 */
export function getHttpService(): HttpService {
  return getRegisteredService<HttpService>(SERVICE_KEYS.HTTP_SERVICE);
}

export function getAthleteData(): AthleteData {
  return getRegisteredService<AthleteData>(SERVICE_KEYS.ATHLETE_DATA);
}

export function getCompetitionData(): CompetitionData {
  return getRegisteredService<CompetitionData>(SERVICE_KEYS.COMPETITION_DATA);
}

export function getConfigData(): ConfigData {
  return getRegisteredService<ConfigData>(SERVICE_KEYS.CONFIG_DATA);
}

export function getAthleteService(): AthleteService {
  return getRegisteredService<AthleteService>(SERVICE_KEYS.ATHLETE_SERVICE);
}

export function getCompetitionService(): CompetitionService {
  return getRegisteredService<CompetitionService>(SERVICE_KEYS.COMPETITION_SERVICE);
}

export function getConfigService(): ConfigService {
  return getRegisteredService<ConfigService>(SERVICE_KEYS.CONFIG_SERVICE);
}

export function getAthleteCompSchedService(): AthleteCompSchedService {
  return getRegisteredService<AthleteCompSchedService>(SERVICE_KEYS.ATHLETE_COMP_SCHED_SERVICE);
}

export function getAthleteCompSchedData(): AthleteCompSchedData {
  return getRegisteredService<AthleteCompSchedData>(SERVICE_KEYS.ATHLETE_COMP_SCHED_DATA);
}

export function getSecondaryService(): SecondaryService {
  return getRegisteredService<SecondaryService>(SERVICE_KEYS.SECONDARY_SERVICE);
}

export function getCommonService(): CommonService {
  return getRegisteredService<CommonService>(SERVICE_KEYS.COMMON_SERVICE);
}

/**
 * Debug function to list all registered services
 */
export function debugServices(): void {
  const container = ServiceContainer.getInstance();
  const services = container.getRegisteredServices();
  
  console.log('[ServiceRegistry] Registered services:');
  services.forEach(key => {
    const info = container.getServiceInfo(key);
    console.log(`  - ${key}: singleton=${info?.singleton}, instantiated=${!!info?.instance}`);
  });
}